import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  Fade,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PlaceIcon from "@mui/icons-material/PlaceOutlined";
import WorkIcon from "@mui/icons-material/Work";
import PersonIcon from "@mui/icons-material/Person";
import { Postrequestcall } from "../../request/Postrequest";
import {
  FETCH_CLIENT_FIXED_ASSETS_LIST,
  FETCH_CLIENT_JOBS,
  FETCH_CLIENTS_EMPLOYEES_V2,
  FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES,
  SAVE_CLIENT_FIXED_ASSET_STATUSES,
  SAVE_CLIENT_FIXED_ASSET_PICTURES,
} from "../../constant/Apipath";
import { showErrorToast, showSuccessToast } from "../../toast/toast";
import DynamicFormField from "./components/DynamicFormField";
import { getFallbackFormFields } from "./constant/formFields";

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      display: "block",
      fontSize: "0.68rem",
      fontWeight: 700,
      letterSpacing: "0.1em",
      color: "#94a3b8",
      mb: 2,
      mt: 0.5,
    }}
  >
    {children}
  </Typography>
);

const LOCATION_TYPES = [
  { id: "place", label: "Place", icon: PlaceIcon },
  { id: "job", label: "Job", icon: WorkIcon },
  { id: "employee", label: "Employee", icon: PersonIcon },
];

const DROPDOWN_PAGE_SIZE = 20;

const normalizeSelectedAsset = (asset) => {
  if (!asset) return null;

  const assetId = asset.AssetId ?? asset.AssetID ?? asset.id ?? asset.Id ?? 0;
  const assetName =
    asset.AssetName ?? asset.AssetCode ?? asset.asset ?? asset.name ?? asset.label ?? "";

  return {
    id: assetId,
    label: assetName,
    AssetId: assetId,
    AssetCode: asset.AssetCode ?? asset.assetCode ?? "",
    AssetName: asset.AssetName ?? assetName,
    CategoryName: asset.CategoryName ?? "",
    SubCategoryName: asset.SubCategoryName ?? "",
    SerialNo: asset.SerialNo ?? "",
    AssignedToName: asset.AssignedToName ?? "",
    Location: asset.Location ?? "",
    LocationType: asset.LocationType ?? "",
    LocationTitle: asset.LocationTitle ?? "",
    StatusTypeName: asset.StatusTypeName ?? asset.StatusName ?? "",
    AssetStatusId: asset.AssetStatusId ?? 0,
    StatusId: asset.StatusId ?? 0,
    StatusName: asset.StatusName ?? asset.StatusTypeName ?? "",
    BarCode: asset.BarCode ?? "",
    PurchaseDate: asset.PurchaseDate ?? null,
    WarrantyExpireDate: asset.WarrantyExpireDate ?? null,
  };
};

const TransferAssetModal = ({ open, onClose, selectedAsset }) => {
  const { loginData, selectedClientData } = useSelector(
    (state) => state?.main
  );
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loadingFields, setLoadingFields] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [dropdownLoading, setDropdownLoading] = useState({});
  const [dropdownLoadingMore, setDropdownLoadingMore] = useState({});
  const [dropdownPage, setDropdownPage] = useState({});
  const [dropdownHasMore, setDropdownHasMore] = useState({});
  const [dropdownSearchText, setDropdownSearchText] = useState({});
  const [images, setImages] = useState([]);

  const currentAsset = normalizeSelectedAsset(selectedAsset);
  const accent = selectedClientData?.AccentColor || "#6366f1";

  const fetchFormFields = useCallback(async () => {
    setLoadingFields(true);
    try {
      const res = await Postrequestcall(
        "Client/FetchClientFormFieldSettings2",
        {
          PageNr: 1,
          NrOfRecPerPage: 20,
          FullSearch: "",
          UserId: loginData?.userInformation?.UserId || 1,
          IncludeRecordNr: "true",
          TypeOfObjectReturned: "",
          SearchList: [
            {
              ClientId: selectedClientData?.ClientId,
              appId: 21,
              AppFormKey: "TRANSFER_ASSET",
              // formName: "TransferAsset",
              isVisible: true,
            },
          ],
          SortList: [{}],
        },
        loginData?.accessToken
      );

      const fields =
        res?.status === 200 && res?.data?.data?.length > 0
          ? res.data.data
          : getFallbackFormFields("TransferAsset");

      // Ensure each field has a unique fieldKey (append id for duplicates)
      // but keep the original key on origFieldKey so lookups (dropdowns, logic)
      // can still use the server-provided fieldKeys like STATUS, ASSET, etc.
      const KNOWN = ["ASSET", "JOB", "EMPLOYEE", "SUPERVISOR", "STATUS", "TO_LOCATION", "LOCATION_TYPE", "UPLOAD_PICTURES"];
      const inferLogicalKey = (f) => {
        const s = (f.fieldKey || f.fieldName || f.fieldLabel || "").toString();
        for (const k of KNOWN) {
          const re = new RegExp("\\b" + k.replace("_", "\\s*") + "\\b", "i");
          if (re.test(s)) return k;
        }
        if (/employee/i.test(s)) return "EMPLOYEE";
        if (/supervisor/i.test(s)) return "SUPERVISOR";
        if (/status/i.test(s)) return "STATUS";
        if (/asset/i.test(s)) return "ASSET";
        return f.fieldKey || `FIELD_${f.id}`;
      };

      const ensureUniqueKeys = (flds) => {
        const counts = {};
        return flds.map((f) => {
          const original = inferLogicalKey(f);
          counts[original] = (counts[original] || 0) + 1;
          const uniqueKey = counts[original] > 1 ? `${original}_${f.id}` : original;
          return { ...f, fieldKey: uniqueKey, origFieldKey: original };
        });
      };

      const normalizedFields = ensureUniqueKeys(fields);
      setFormFields(normalizedFields);

      // debug
      console.log("fetchFormFields - normalizedFields:", normalizedFields.map((f) => ({ id: f.id, fieldKey: f.fieldKey, orig: f.origFieldKey, label: f.fieldName || f.fieldLabel })));

      // Build initial formData, preserving known keys and setting defaults for others
      console.log("fetchFormFields - building initial formData with currentAsset:", currentAsset);
      const initialData = {
        ASSET: currentAsset,
        LOCATION_TYPE: "",
        TO_LOCATION: "",
        JOB: null,
        EMPLOYEE: null,
        TRANSFER_DATE: new Date().toISOString().split("T")[0],
        STATUS: null,
        SUPERVISOR: null,
        NOTES: "",
      };

      normalizedFields.forEach((f) => {
        if (!(f.fieldKey in initialData)) {
          const t = f.fieldType;
          initialData[f.fieldKey] =
            t === "Number" ? 0 : t === "MultilineText" ? "" : t === "Text" ? "" : null;
        }
      });

      setFormData(initialData);

      fetchDropdownData(FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES, "STATUS");
      fetchDropdownData(FETCH_CLIENTS_EMPLOYEES_V2, "SUPERVISOR");
      // also fetch EMPLOYEE options up-front for debugging
      fetchDropdownData(FETCH_CLIENTS_EMPLOYEES_V2, "EMPLOYEE");
      if (!currentAsset)
        fetchDropdownData(FETCH_CLIENT_FIXED_ASSETS_LIST, "ASSET");
    } catch (e) {
      setFormFields(getFallbackFormFields("TransferAsset"));
    } finally {
      setLoadingFields(false);
    }
  }, [open, selectedClientData, loginData, currentAsset]);

  useEffect(() => {
    if (open) {
      fetchFormFields();
      setImages([]);
      setDropdownOptions({});
      setDropdownLoading({});
      setDropdownLoadingMore({});
      setDropdownPage({});
      setDropdownHasMore({});
      setDropdownSearchText({});
    }
  }, [open]);

  const fetchDropdownData = async (
    url,
    fieldKey,
    extraSearch = {},
    page = 1,
    append = false,
    searchText = ""
  ) => {
    console.log(`fetchDropdownData start: ${fieldKey}`);
    if (append) {
      setDropdownLoadingMore((prev) => ({ ...prev, [fieldKey]: true }));
    }
    setDropdownLoading((prev) => ({ ...prev, [fieldKey]: true }));
    try {
      // Special-case ASSET: use the same API and payload structure as FixedAssetList
      if (fieldKey === "ASSET") {
        const clientId = selectedClientData?.ClientId;
        const searchList = [{ clientId }];
        const shouldRestrictToSelf = selectedClientData?.IsAdmin !== 1 && selectedClientData?.ClientEmployeeId;
        if (shouldRestrictToSelf) {
          searchList.push({ locationType: "employee", location: selectedClientData?.ClientEmployeeId });
        }
        // include any extraSearch params
        if (extraSearch && typeof extraSearch === "object" && Object.keys(extraSearch).length) {
          searchList.push(extraSearch);
        }

        const payload = {
          NrOfRecPerPage: DROPDOWN_PAGE_SIZE,
          PageNr: page,
          FullSearch: searchText,
          FetchAllowedRecordsOnly: false,
          SearchList: searchList,
          SortList: [{ FieldName: "id", Direction: "Desc" }],
          TypeOfObjectReturned: "",
          IncludeRecordNr: true,
          UserId: loginData?.userInformation?.UserId,
        };

        console.log(`fetchDropdownData payload for ASSET:`, payload);
        const res = await Postrequestcall(FETCH_CLIENT_FIXED_ASSETS_LIST, payload, loginData?.accessToken);
        console.log(`fetchDropdownData response ASSET:`, res?.status);
        if (res?.status === 200) {
          const body = res?.data || {};
          const items = Array.isArray(body?.data)
            ? body.data
            : Array.isArray(body?.Data)
              ? body.Data
              : Array.isArray(body?.Result)
                ? body.Result
                : Array.isArray(body)
                  ? body
                  : [];
          const mapped = items.map((a) => ({ id: a.id, label: a.asset, AssetCode: a.assetCode, AssetId: a.id, AssetName: a.asset }));
          console.log(`fetchDropdownData mapped ASSET:`, mapped.slice(0, 12));
          const totalRecords = Number(body?.nrOfRecords ?? body?.RecordNr ?? 0);
          setDropdownOptions((prev) => ({
            ...prev,
            ASSET: append ? [...(prev.ASSET || []), ...mapped] : mapped,
          }));
          setDropdownPage((prev) => ({ ...prev, ASSET: page }));
          setDropdownSearchText((prev) => ({ ...prev, ASSET: searchText }));
          setDropdownHasMore((prev) => ({
            ...prev,
            ASSET: totalRecords > 0 ? page * DROPDOWN_PAGE_SIZE < totalRecords : mapped.length === DROPDOWN_PAGE_SIZE,
          }));
        }

        return;
      }

      // Default dropdown flow
      const payload = {
        PageNr: page,
        NrOfRecPerPage: DROPDOWN_PAGE_SIZE,
        FullSearch: searchText,
        SearchList: [{ ClientId: selectedClientData?.ClientId, active: true, ...extraSearch }],
        UserId: loginData?.userInformation?.UserId,
        IncludeRecordNr: true,
        FetchAllowedRecordsOnly: true,
      };
      console.log(`fetchDropdownData payload for ${fieldKey}:`, payload);
      const res = await Postrequestcall(url, payload, loginData?.accessToken);
      console.log(`fetchDropdownData response ${fieldKey}:`, res?.status, res?.data?.data?.length);
      if (res?.status === 200) {
        let data = res.data.data || [];
        if (fieldKey === "JOB")
          data = data.map((j) => ({
            id: j.id,
            label: `${j.jobCode} - ${j.name}`,
          }));
        else if (fieldKey === "EMPLOYEE" || fieldKey === "SUPERVISOR")
          data = data.map((e) => ({
            id: e.id,
            label: `${e.firstName || ""} ${e.lastName || ""}`.trim(),
            firstName: e.firstName,
            lastName: e.lastName,
          }));
        else data = data.map((i) => ({ id: i.id, label: i.name }));

        console.log(`fetchDropdownData mapped ${fieldKey}:`, data.slice(0, 8));
        const totalRecords = Number(res?.data?.nrOfRecords ?? res?.data?.RecordNr ?? 0);
        setDropdownOptions((prev) => ({
          ...prev,
          [fieldKey]: append ? [...(prev[fieldKey] || []), ...data] : data,
        }));
        setDropdownPage((prev) => ({ ...prev, [fieldKey]: page }));
        setDropdownSearchText((prev) => ({ ...prev, [fieldKey]: searchText }));
        setDropdownHasMore((prev) => ({
          ...prev,
          [fieldKey]: totalRecords > 0 ? page * DROPDOWN_PAGE_SIZE < totalRecords : data.length === DROPDOWN_PAGE_SIZE,
        }));
      }
    } catch (e) {
      console.error(`fetchDropdownData error ${fieldKey}:`, e);
    } finally {
      setDropdownLoading((prev) => ({ ...prev, [fieldKey]: false }));
      if (append) {
        setDropdownLoadingMore((prev) => ({ ...prev, [fieldKey]: false }));
      }
      console.log(`fetchDropdownData end: ${fieldKey}`);
    }
  };

  const handleDropdownSearch = (fieldKey, searchText) => {
    const dropdownUrls = {
      ASSET: FETCH_CLIENT_FIXED_ASSETS_LIST,
      JOB: FETCH_CLIENT_JOBS,
      EMPLOYEE: FETCH_CLIENTS_EMPLOYEES_V2,
      SUPERVISOR: FETCH_CLIENTS_EMPLOYEES_V2,
      STATUS: FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES,
    };

    const url = dropdownUrls[fieldKey];
    if (!url) return;

    fetchDropdownData(url, fieldKey, {}, 1, false, searchText);
  };

  const handleDropdownLoadMore = (fieldKey) => {
    const dropdownUrls = {
      ASSET: FETCH_CLIENT_FIXED_ASSETS_LIST,
      JOB: FETCH_CLIENT_JOBS,
      EMPLOYEE: FETCH_CLIENTS_EMPLOYEES_V2,
      SUPERVISOR: FETCH_CLIENTS_EMPLOYEES_V2,
      STATUS: FETCH_CLIENT_FIXED_ASSET_STATUS_TYPES,
    };

    const url = dropdownUrls[fieldKey];
    if (!url || dropdownLoading[fieldKey] || !dropdownHasMore[fieldKey]) return;

    const nextPage = (dropdownPage[fieldKey] || 1) + 1;
    const searchText = dropdownSearchText[fieldKey] || "";
    fetchDropdownData(url, fieldKey, {}, nextPage, true, searchText);
  };

  const handleFieldChange = (fieldKey, value, origKey) => {
    const lookupKey = origKey || fieldKey;

    // Store value under both the rendered (unique) fieldKey and the logical/orig key
    setFormData((prev) => ({ ...prev, [fieldKey]: value, [lookupKey]: value }));

    // Special handling when the logical field key is LOCATION_TYPE
    if (lookupKey === "LOCATION_TYPE") {
      setFormData((prev) => ({
        ...prev,
        JOB: null,
        EMPLOYEE: null,
        TO_LOCATION: "",
        [fieldKey]: value,
        LOCATION_TYPE: value,
      }));
      if (value === "job") fetchDropdownData(FETCH_CLIENT_JOBS, "JOB");
      if (value === "employee")
        fetchDropdownData(FETCH_CLIENTS_EMPLOYEES_V2, "EMPLOYEE");
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    const mapped = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...mapped]);
  };

  const validateForm = () => {
    const isFilled = (value) => {
      if (value === null || value === undefined || value === "") return false;
      if (typeof value === "object") return Boolean(value.id ?? value.AssetId ?? value.value);
      return true;
    };

    for (const field of formFields) {
      if (!field.isVisible || !field.isRequired) continue;

      const fieldKey = field.origFieldKey || field.fieldKey;
      if (fieldKey === "UPLOAD_PICTURES" && images.length === 0) {
        showErrorToast(`${field.fieldName || fieldKey} is required`);
        return false;
      }
      if (fieldKey != "UPLOAD_PICTURES" &&!isFilled(formData[fieldKey])) {
        showErrorToast(`${field.fieldName || fieldKey} is required`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      let locationValue = formData.TO_LOCATION;
      let locationTitle = formData.TO_LOCATION;

      if (formData.LOCATION_TYPE === "job" && formData.JOB) {
        locationValue = formData.JOB.id;
        locationTitle = formData.JOB.label;
      } else if (formData.LOCATION_TYPE === "employee" && formData.EMPLOYEE) {
        locationValue = formData.EMPLOYEE.id;
        locationTitle = formData.EMPLOYEE.label;
      }

      const payload = {
        ReturnRecordId: true,
        ReturnRecordError: true,
        UserId: loginData?.userInformation?.UserId,
        SaveList: [
          {
            Id: 0,
            AssetId: formData.ASSET?.AssetId || formData.ASSET?.id,
            TransferDate: new Date(formData.TRANSFER_DATE).toISOString(),
            LocationType: formData.LOCATION_TYPE,
            Location: locationValue,
            LocationTitle: locationTitle,
            StatusId: formData.STATUS?.id || formData.STATUS,
            Comments: formData.NOTES,
            ClientId: selectedClientData?.ClientId,
            SuperVisorId: formData.SUPERVISOR?.id || 0,
            ModifiedBy: loginData?.userInformation?.UserId,
          },
        ],
      };

      const res = await Postrequestcall(
        SAVE_CLIENT_FIXED_ASSET_STATUSES,
        payload,
        loginData?.accessToken
      );
      const statusId =
        res?.data?.data?.[0]?.objData?.id || res?.data?.RecordId;

      if (statusId && images.length > 0) {
        for (const img of images) {
          const fd = new FormData();
          fd.append("Id", 0);
          fd.append("AssetId", formData.ASSET?.AssetId || formData.ASSET?.id || 0);
          fd.append("AssetStatusId", statusId);
          fd.append("PictureUrl", img.file);
          fd.append("PictureTypeId", 2);
          fd.append("ClientId", selectedClientData?.ClientId);
          fd.append("ModifiedBy", loginData?.userInformation?.UserId);
          fd.append("UserId", loginData?.userInformation?.UserId);
          await Postrequestcall(
            SAVE_CLIENT_FIXED_ASSET_PICTURES,
            fd,
            loginData?.accessToken,
            true
          );
        }
      }

      showSuccessToast("Transfer saved successfully");
      onClose(true);
    } catch (e) {
      showErrorToast(e.message || "Failed to save transfer");
    } finally {
      setSaving(false);
    }
  };

  const visibleFields = formFields
    .filter((f) => f.isVisible !== false)
    .sort(
      (a, b) =>
        (parseInt(a.displayOrder) || 0) - (parseInt(b.displayOrder) || 0)
    );

  // Separate fields into groups (excluding LOCATION_TYPE which we render custom)
  const getOrigKey = (f) => f.origFieldKey || f.fieldKey;
console.log("visibleFields",visibleFields)
  const assetFields = visibleFields.filter((f) =>
    ["ASSET", "TRANSFER_DATE", "STATUS", "SUPERVISOR"].includes(getOrigKey(f))
  );
  const locationFields = visibleFields.filter((f) =>
    ["TO_LOCATION", "JOB", "EMPLOYEE"].includes(getOrigKey(f))
  );
  const otherFields = visibleFields.filter(
    (f) =>
      !["ASSET", "TRANSFER_DATE", "STATUS", "SUPERVISOR", "TO_LOCATION", "JOB", "EMPLOYEE", "LOCATION_TYPE", "UPLOAD_PICTURES"].includes(getOrigKey(f))
  );
  const imageField = visibleFields.find((f) => getOrigKey(f) === "UPLOAD_PICTURES");

  const renderField = (field) => {
    // If this field came from server use origFieldKey for logical checks
    const logicalKey = field.origFieldKey || field.fieldKey;
    const fieldToRender =
      logicalKey === "ASSET" && currentAsset
        ? { ...field, isReadOnly: true }
        : field;

    if (logicalKey === "TO_LOCATION" && formData.LOCATION_TYPE !== "place") return null;
    if (logicalKey === "JOB" && formData.LOCATION_TYPE !== "job") return null;
    if (logicalKey === "EMPLOYEE" && formData.LOCATION_TYPE !== "employee") return null;

    const opts = dropdownOptions[logicalKey] || [];
    const rawValue =
      formData[field.fieldKey] !== undefined
        ? formData[field.fieldKey]
        : formData[logicalKey];

    // Normalize value so Autocomplete can match against options properly
    let normalizedValue = rawValue;
    if (rawValue) {
      if (typeof rawValue === "object") {
        if (rawValue.id !== undefined) normalizedValue = rawValue;
        else if (rawValue.AssetId !== undefined) {
          // try to find matching option by id
          const found = opts.find((o) => o.id == rawValue.AssetId);
          normalizedValue =
            found || {
              id: rawValue.AssetId,
              label:
                rawValue.AssetName ||
                rawValue.AssetCode ||
                rawValue.asset ||
                rawValue.name ||
                rawValue.label ||
                "",
              AssetId: rawValue.AssetId,
              AssetCode: rawValue.AssetCode || "",
              AssetName: rawValue.AssetName || rawValue.asset || rawValue.name || rawValue.label || "",
            };
        } else {
          // fallback: try to match by label or name
          const found = opts.find((o) => o.label == rawValue.label || o.label == rawValue.name);
          normalizedValue = found || rawValue;
        }
      } else {
        // rawValue is primitive (id or label)
        const found = opts.find((o) => o.id == rawValue || o.label == rawValue);
        normalizedValue = found || rawValue;
      }
    }
console.log(`renderField - ${field} - normalizedValue:`, normalizedValue);
    return (
      <Grid
        size={{
          xs: 12,
          sm: field.fieldType === "MultilineText" ? 12 : 6,
        }}
        key={field.id || field.fieldKey}
      >
        <DynamicFormField
          field={fieldToRender}
          value={normalizedValue}
          onChange={
            field.fieldType === "Dropdown"
              ? undefined
              : (val) => handleFieldChange(field.fieldKey, val, field.origFieldKey)
          }
          options={opts}
          loading={dropdownLoading[logicalKey] || false}
          loadingMore={dropdownLoadingMore[logicalKey] || false}
          onSelect={(val) => handleFieldChange(field.fieldKey, val, field.origFieldKey)}
          onSearch={(searchText) => handleDropdownSearch(logicalKey, searchText)}
          onLoadMore={() => handleDropdownLoadMore(logicalKey)}
          hasMore={dropdownHasMore[logicalKey] || false}
          accentColor={accent}
        />
      </Grid>
    );
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={() => onClose(false)}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 250 }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow:
            "0 25px 50px -12px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5,
            background: `linear-gradient(135deg, ${accent}18 0%, ${accent}06 100%)`,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                bgcolor: accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 12px ${accent}44`,
              }}
            >
              <SwapHorizIcon sx={{ color: "#fff", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b", lineHeight: 1.2 }}
              >
                Transfer Asset
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Move an asset to a new location or assignee
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => onClose(false)}
            size="small"
            sx={{
              color: "#64748b",
              bgcolor: "#f1f5f9",
              borderRadius: "8px",
              "&:hover": { bgcolor: "#e2e8f0", color: "#1e293b" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          p: 0,
          bgcolor: "#f8fafc",
          overflowY: "auto",
          maxHeight: "70vh",
        }}
      >
        {loadingFields ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 8,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: accent }} size={36} thickness={3} />
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              Loading form…
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Asset & Transfer details */}
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                p: 3,
              }}
            >
              <SectionLabel>Transfer Details</SectionLabel>
              {currentAsset && (
                <Box
                  sx={{
                    mb: 2.5,
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: `${accent}0d`,
                    border: `1px solid ${accent}22`,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}
                  >
                    Selected Asset
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                        Asset Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                        {currentAsset.AssetName || "-"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                        Asset Code
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                        {currentAsset.AssetCode || "-"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                        Status
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                        {currentAsset.StatusName || currentAsset.StatusTypeName || "-"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                        Category
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                        {currentAsset.CategoryName || "-"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                        Sub Category
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                        {currentAsset.SubCategoryName || "-"}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                        Location
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                        {currentAsset.LocationTitle || currentAsset.Location || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
              <Grid container spacing={2.5}>
                {assetFields.map(renderField)}
                {otherFields.map(renderField)}
              </Grid>
            </Box>

            {/* Destination */}
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                p: 3,
              }}
            >
              <SectionLabel>Destination</SectionLabel>

              {visibleFields.some((f) => getOrigKey(f) === "LOCATION_TYPE") && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1.5, color: "#374151", fontWeight: 500, fontSize: "0.85rem" }}
                  >
                    Transfer to *
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={formData.LOCATION_TYPE || ""}
                    onChange={(_, val) => {
                      if (val !== null) handleFieldChange("LOCATION_TYPE", val);
                    }}
                    sx={{
                      display: "flex",
                      gap: 1,
                      "& .MuiToggleButtonGroup-grouped": {
                        border: "1.5px solid #e2e8f0 !important",
                        borderRadius: "10px !important",
                        flex: 1,
                        px: 2,
                        py: 1.2,
                        textTransform: "none",
                        fontSize: "0.85rem",
                        fontWeight: 500,
                        color: "#64748b",
                        transition: "all 0.18s",
                        "&.Mui-selected": {
                          bgcolor: `${accent}14`,
                          borderColor: `${accent} !important`,
                          color: accent,
                          fontWeight: 600,
                          boxShadow: `0 0 0 1px ${accent}`,
                        },
                        "&:hover:not(.Mui-selected)": {
                          bgcolor: "#f8fafc",
                        },
                      },
                    }}
                  >
                    {LOCATION_TYPES.map(({ id, label, icon: Icon }) => (
                      <ToggleButton key={id} value={id}>
                        <Icon sx={{ fontSize: 17, mr: 0.75 }} />
                        {label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              )}

              {formData.LOCATION_TYPE && (
                <Grid container spacing={2.5}>
                  {locationFields.map(renderField)}
                </Grid>
              )}

              {!formData.LOCATION_TYPE && (
                <Box
                  sx={{
                    py: 3,
                    textAlign: "center",
                    color: "#cbd5e1",
                    fontSize: "0.83rem",
                    borderRadius: "8px",
                    bgcolor: "#f8fafc",
                    border: "1.5px dashed #e2e8f0",
                  }}
                >
                  Select a destination type above
                </Box>
              )}
            </Box>

            {/* Photos */}
            {imageField && (
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  p: 3,
                }}
              >
                <SectionLabel>Photos</SectionLabel>
                <DynamicFormField
                  field={imageField}
                  value={formData[imageField.fieldKey]}
                  onChange={(val) => handleFieldChange(imageField.fieldKey, val)}
                  accentColor={accent}
                  uploadedImages={images}
                  onImageAdd={handleImageChange}
                  onImageRemove={(i) =>
                    setImages((prev) => prev.filter((_, idx) => idx !== i))
                  }
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#fff",
          borderTop: "1px solid #f1f5f9",
          gap: 1.5,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
            * Required fields
          </Typography>
        </Box>
        <Button
          onClick={() => onClose(false)}
          variant="outlined"
          sx={{
            textTransform: "none",
            px: 3,
            py: 1,
            borderRadius: "9px",
            borderColor: "#e2e8f0",
            color: "#64748b",
            fontWeight: 500,
            "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={handleSubmit}
          sx={{
            textTransform: "none",
            px: 4,
            py: 1,
            borderRadius: "9px",
            fontWeight: 600,
            boxShadow: `0 4px 12px ${accent}44`,
            bgcolor: accent,
            "&:hover": { bgcolor: accent, filter: "brightness(0.92)" },
            "&:disabled": { opacity: 0.7 },
            minWidth: 150,
          }}
        >
          {saving ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} color="inherit" thickness={3} />
              Transferring…
            </Box>
          ) : (
            "Submit Transfer"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferAssetModal;